import { useState } from "react";
import { Modal, Form, Input, InputNumber, message } from "antd";

const { TextArea } = Input;

/**
 * Edit Custom Fee Modal
 *
 * Allows editing description and amount for entire fee group
 * Applies to all students in the group
 */
const EditCustomFeeModal = ({
  visible,
  onCancel,
  onSuccess,
  groupData,
  updateCustomFee,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      // Update all request IDs in this group sequentially
      for (const requestId of groupData.customFeeRequestIds) {
        const data = {
          description: values.description,
          amount: parseFloat(values.amount),
          notes: values.notes || "",
        };

        const result = await updateCustomFee(requestId, data);
        if (result !== true) {
          throw new Error("Failed to update request");
        }
      }

      message.success(
        `Updated ${groupData.customFeeRequestIds.length} request(s) in the group`,
      );
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error("Update error:", error);
      message.error("Failed to update custom fee request");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Set initial form values when modal opens
  if (visible && groupData && form) {
    setTimeout(() => {
      form.setFieldsValue({
        description: groupData.description,
        amount: parseFloat(groupData.amount),
        notes: groupData.notes || "",
      });
    }, 0);
  }

  return (
    <Modal
      title={`Edit Custom Fee Request - ${groupData?.students?.length || 0} Student(s)`}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="Update"
      cancelText="Cancel"
    >
      <Form form={form} layout="vertical">
        {/* Display Info */}
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            backgroundColor: "#f5f5f5",
            borderRadius: 4,
          }}
        >
          <p style={{ marginBottom: 8 }}>
            <strong>Fee Type:</strong> {groupData?.feeTypeName}
          </p>
          <p style={{ marginBottom: 8 }}>
            <strong>Academic Year:</strong> {groupData?.acadyr}
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>Students Affected:</strong>{" "}
            {groupData?.students?.length || 0}
          </p>
        </div>

        {/* Description */}
        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: "Please enter description" }]}
        >
          <TextArea rows={2} placeholder="Describe this custom fee" />
        </Form.Item>

        {/* Amount */}
        <Form.Item
          name="amount"
          label="Amount"
          rules={[
            { required: true, message: "Please enter amount" },
            {
              type: "number",
              min: 1,
              message: "Amount must be greater than 0",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="0"
            min={0}
            precision={0}
            formatter={(value) => {
              if (!value) return "";
              return `UGX ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }}
            parser={(value) => {
              const parsed = value.replace(/UGX\s?|(,)/g, "");
              return parsed === "" ? 0 : parseInt(parsed);
            }}
          />
        </Form.Item>

        {/* Notes */}
        <Form.Item name="notes" label="Notes (Optional)">
          <TextArea rows={2} placeholder="Any additional notes" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCustomFeeModal;
